<template>
  <nav 
    class="navbar fixed top-0 left-0 right-0 z-50 bg-gradient-to-r from-[#353899]/95 to-[#3a3494]/95 backdrop-blur-md border-b border-[#353899]/20 shadow-sm" 
    :dir="currentLang === 'ar' ? 'rtl' : 'ltr'"
  >
    <div class="container mx-auto px-4 sm:px-6 lg:px-8">
      <div class="flex items-center justify-between h-16">
        
        <!-- Logo Section -->
        <div class="flex-shrink-0">
          <router-link to="/" class="flex items-center group">
            <img 
              src="../assets/logo_salasah.sa.png" 
              alt="Logo" 
              class="h-10 w-auto transition-transform group-hover:scale-105"
            >
          </router-link>
        </div>

        <!-- Desktop Navigation Links -->
        <div class="hidden lg:flex items-center justify-center flex-1 px-8">
          <div class="flex items-center space-x-1 rtl:space-x-reverse">
            <router-link 
              v-for="link in links" 
              :key="link.path"
              :to="link.path"
              class="nav-link px-4 py-2 text-sm font-medium rounded-lg transition-all duration-200 relative"
              :class="{ 
                'active-link bg-white text-[#0f4640] shadow-md': $route.path === link.path,
                'text-white hover:text-black hover:bg-white/90': $route.path !== link.path
              }"
            >
              {{ $t(`navbar.links.${link.key}`) }}
              <span 
                v-if="$route.path === link.path" 
                class="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-[#0f4640] rounded-full"
              ></span>
            </router-link>
          </div>
        </div>

        <!-- Right Section: Language Toggle & Login -->
        <div class="hidden lg:flex items-center space-x-4 rtl:space-x-reverse">
          
          <!-- Language Toggle -->
          <div class="flex items-center bg-white/20 rounded-lg p-1">
            <button 
              @click="changeLanguage('ar')" 
              class="lang-btn px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200"
              :class="currentLang === 'ar' 
                ? 'bg-white text-[#0f4640] shadow-sm' 
                : 'text-white/90 hover:text-white hover:bg-white/20'"
            >
              عربي
            </button>
            <button 
              @click="changeLanguage('en')" 
              class="lang-btn px-3 py-1.5 text-sm font-medium rounded-md transition-all duration-200"
              :class="currentLang === 'en' 
                ? 'bg-white text-[#0f4640] shadow-sm' 
                : 'text-white/90 hover:text-white hover:bg-white/20'"
            >
              EN
            </button>
          </div>
          
          <!-- Login Button -->
          <a 
            href="https://admin.salasah.sa/#/login"
            class="login-btn inline-flex items-center px-4 py-2 bg-white text-[#0f4640] text-sm font-semibold rounded-lg hover:bg-white/90 transition-all duration-200 shadow-sm hover:shadow-md transform hover:-translate-y-0.5"
          >
            <svg class="w-4 h-4 mr-2 rtl:ml-2 rtl:mr-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
            </svg>
            {{ $t('navbar.login') }}
          </a>
        </div>

        <!-- Mobile Menu Button -->
        <div class="lg:hidden">
          <button 
            @click="isOpen = !isOpen" 
            class="mobile-menu-btn p-2 text-white/90 hover:text-white hover:bg-white/10 rounded-lg transition-all duration-200"
            :aria-expanded="isOpen"
            aria-label="Toggle menu"
          >
            <svg class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path v-if="!isOpen" stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
              <path v-else stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>
    </div>

    <!-- Mobile Menu Overlay -->
    <transition name="mobile-menu">
      <div 
        v-if="isOpen" 
        class="lg:hidden fixed inset-x-0 top-16 bg-gradient-to-br from-[#353899] to-[#3a3494] border-b border-[#353899]/20 shadow-lg"
      >
        <div class="px-4 py-6 space-y-4">
          
          <!-- Mobile Navigation Links -->
          <div class="space-y-2">
            <router-link 
              v-for="link in links" 
              :key="link.path"
              :to="link.path"
              class="mobile-nav-link block px-4 py-3 text-base font-medium text-white hover:text-black hover:bg-white/90 rounded-lg transition-all duration-200"
              :class="{ 
                'bg-white text-[#0f4640]': $route.path === link.path 
              }"
              @click="isOpen = false"
            >
              {{ $t(`navbar.links.${link.key}`) }}
            </router-link>
          </div>

          <!-- Mobile Language & Login Section -->
          <div class="pt-4 border-t border-white/20 space-y-4">
            
            <!-- Language Toggle -->
            <div class="flex items-center justify-center">
              <div class="flex bg-white/20 rounded-lg p-1">
                <button 
                  @click="changeLanguage('ar')" 
                  class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
                  :class="currentLang === 'ar' 
                    ? 'bg-white text-[#0f4640] shadow-sm' 
                    : 'text-white/90 hover:text-white hover:bg-white/20'"
                >
                  عربي
                </button>
                <button 
                  @click="changeLanguage('en')" 
                  class="flex-1 px-4 py-2 text-sm font-medium rounded-md transition-all duration-200"
                  :class="currentLang === 'en' 
                    ? 'bg-white text-[#0f4640] shadow-sm' 
                    : 'text-white/90 hover:text-white hover:bg-white/20'"
                >
                  EN
                </button>
              </div>
            </div>
            
            <!-- Login Button -->
            <a 
              href="https://admin.salasah.sa/#/login"
              class="mobile-login-btn flex items-center justify-center w-full px-4 py-3 bg-white text-[#0f4640] text-base font-semibold rounded-lg hover:bg-white/90 transition-all duration-200"
              @click="isOpen = false"
            >
              <svg class="w-5 h-5 mr-2 rtl:ml-2 rtl:mr-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1"></path>
              </svg>
              {{ $t('navbar.login') }}
            </a>
          </div>
        </div>
      </div>
    </transition>
  </nav>

  <!-- Spacer for fixed navbar -->
   
</template>

<script>
import { ref, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'

export default {
  name: 'NavBar',
  setup() {
    const { locale } = useI18n()
    const isOpen = ref(false)
    const currentLang = ref(locale.value || 'ar')

    const links = [
      { key: 'home', path: '/' },
      { key: 'workspace', path: '/workspace' },
      { key: 'financial', path: '/financial' },
      { key: 'hr', path: '/hr' },
      { key: 'projects', path: '/projects' },
      { key: 'manufacturing', path: '/manufacturing' },
      { key: 'crm', path: '/crm' },
      { key: 'support', path: '/support' }
    ]

    const changeLanguage = (lang) => {
      currentLang.value = lang
      locale.value = lang
      document.documentElement.dir = lang === 'ar' ? 'rtl' : 'ltr'
      document.documentElement.lang = lang
      
      // Close mobile menu after language change
      isOpen.value = false
    }

    // Watch for language changes
    watch(locale, (newLocale) => {
      currentLang.value = newLocale
      document.documentElement.dir = newLocale === 'ar' ? 'rtl' : 'ltr'
      document.documentElement.lang = newLocale
    })

    // Close mobile menu when clicking outside
    const handleClickOutside = (event) => {
      const navbar = event.target.closest('.navbar')
      if (!navbar && isOpen.value) {
        isOpen.value = false
      }
    }

    onMounted(() => {
      // Set initial language
      changeLanguage(locale.value || 'ar')
      
      // Add click outside listener
      document.addEventListener('click', handleClickOutside)
    })

    return {
      isOpen,
      currentLang,
      links,
      changeLanguage
    }
  }
}
</script>

<style scoped>
/* Navbar Animations */
.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition: all 0.3s ease;
}

.mobile-menu-enter-from {
  opacity: 0;
  transform: translateY(-10px);
}

.mobile-menu-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Active Link Indicator */
.active-link::before {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 20px;
  height: 2px;
  background: currentColor;
  border-radius: 1px;
}

/* Hover Effects */
.nav-link:hover {
  transform: translateY(-1px);
}

.login-btn:hover {
  box-shadow: 0 8px 25px rgba(15, 70, 64, 0.2);
}

.mobile-menu-btn:hover {
  transform: scale(1.05);
}

/* RTL Support */
[dir="rtl"] .space-x-1 > * + * {
  margin-right: 0.25rem;
  margin-left: 0;
}

[dir="rtl"] .space-x-4 > * + * {
  margin-right: 1rem;
  margin-left: 0;
}

/* Responsive Adjustments */
@media (max-width: 1024px) {
  .navbar {
    backdrop-filter: blur(20px);
  }
}

/* Smooth scrolling when navbar links are clicked */
html {
  scroll-padding-top: 4rem;
}

/* Remove any default margins that might cause spacing */
.navbar + * {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Ensure no gap between navbar and content */
.navbar {
  margin-bottom: 0 !important;
}

.navbar + .main-content,
.navbar + * {
  position: relative;
  top: 0;
}

/* Logo hover animation */
.group:hover img {
  filter: brightness(1.1);
}

/* Language button active state */
.lang-btn.active {
  background: white;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Mobile nav link active state */
.mobile-nav-link.router-link-active {
  background: white;
  color: #0f4640;
  box-shadow: 0 4px 12px rgba(255, 255, 255, 0.3);
}

/* Accessibility improvements */
.nav-link:focus,
.lang-btn:focus,
.login-btn:focus,
.mobile-menu-btn:focus {
  outline: 2px solid #0f4640;
  outline-offset: 2px;
}

/* Performance optimization */
.navbar {
  will-change: transform;
}

/* Ensure no spacing issues anywhere */
body > div,
#app > nav + * {
  margin-top: 0 !important;
  padding-top: 0 !important;
}

/* Fix any potential gap from hero sections */
section:first-of-type,
.hero-section {
  margin-top: 0 !important;
  padding-top: 0 !important;
}
</style> 